import { z } from 'zod';
import { IsoDate, IsoDateTime, Phone, Uuid } from '../primitives.js';

/**
 * WS7 phase 4 — appointment waitlist contract. Distinct from the walk-in queue (entities/queue.ts):
 * the queue is "I'm here now, in line today"; the waitlist is "book me if a slot frees up on this
 * DATE for this service (and optionally this staff member)". A client joins when a day is full; a
 * later cancellation OFFERS the freed slot to the best-matching waiting entry via the queue's
 * gap-offer comms machinery (consent-aware, allowance-enforced, pause-never-auto-spend).
 *
 * Sealed-book scoped exactly like an appointment: a waitlist entry carries the renter attribution and
 * is RLS-isolated identically (owner/manager see the floor, a renter sees only their own).
 */

/** Lifecycle of a waitlist entry. */
export const WaitlistStatus = z.enum(['waiting', 'offered', 'booked', 'expired']);
export type WaitlistStatus = z.infer<typeof WaitlistStatus>;

export const WaitlistEntry = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable().describe('sealed-book attribution; null = shared/floor entry'),
    clientId: Uuid.nullable(),
    clientName: z.string().min(1).max(120),
    phone: Phone.nullable().describe('how the gap-offer reaches them; no phone → never messaged'),
    serviceId: Uuid,
    staffId: Uuid.nullable().describe('null = any available stylist'),
    wantedDate: IsoDate.describe('the full day the client wants a slot on'),
    status: WaitlistStatus,
    note: z.string().max(500).nullable(),
    offeredAt: IsoDateTime.nullable().describe('set when this entry was offered a freed slot'),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'WaitlistEntry' });
export type WaitlistEntry = z.infer<typeof WaitlistEntry>;

/**
 * Join the waitlist for a date/service (and optionally a specific staff member). clientId links a
 * known client (sealed isolation flows from it); a walk-up supplies just a name + phone. Phone is
 * how the gap-offer reaches them — without it the entry still queues but is never auto-notified.
 */
export const CreateWaitlistEntry = z
  .object({
    serviceId: Uuid,
    wantedDate: IsoDate,
    staffId: Uuid.nullish().describe('omit / null = any available stylist'),
    clientId: Uuid.nullish(),
    clientName: z.string().min(1).max(120),
    phone: Phone.nullish(),
    note: z.string().max(500).nullish(),
  })
  .meta({ id: 'CreateWaitlistEntry' });
export type CreateWaitlistEntry = z.infer<typeof CreateWaitlistEntry>;

/**
 * Filter for listing waitlist entries: by date, and/or by status (defaults to active entries).
 * NOTE: this is used as a querystring schema, so it carries NO `.meta({ id })` component id — a named
 * component on a query schema makes @fastify/swagger emit an unresolvable $ref for the query params
 * (resolveCommonParams crashes). Every other *Query schema in this package follows the same rule.
 */
export const WaitlistQuery = z.object({
  date: IsoDate.optional().describe('only entries wanting this date'),
  status: WaitlistStatus.optional().describe('defaults to waiting + offered when omitted'),
});
export type WaitlistQuery = z.infer<typeof WaitlistQuery>;

/** Per-day waitlist depth — what staff see on the diary ("3 waiting for Tue 16th"). */
export const WaitlistDepth = z
  .object({
    wantedDate: IsoDate,
    waiting: z.int().describe('entries still in "waiting" for this date'),
    offered: z.int().describe('entries currently offered a slot for this date'),
  })
  .meta({ id: 'WaitlistDepth' });
export type WaitlistDepth = z.infer<typeof WaitlistDepth>;

/**
 * Result of offering a freed slot (offerBestMatch) to the waitlist. Reports the chosen entry (if any)
 * and whether the gap-offer message actually went out — false when the entry has no phone or the
 * salon's comms allowance is paused at cap (we pause, never auto-spend).
 */
export const WaitlistOfferResult = z
  .object({
    matched: WaitlistEntry.nullable().describe('the best-matching entry offered the slot, if any'),
    notified: z.boolean().describe('true only if a gap-offer message was actually sent'),
  })
  .meta({ id: 'WaitlistOfferResult' });
export type WaitlistOfferResult = z.infer<typeof WaitlistOfferResult>;

// ── WS10(a) — manual waitlist ACTIONS (operator-driven offer / convert / expire) ───────────────────
// The offer-on-cancel side is automatic (offerBestMatch); these are the manual operator counterparts an
// operator triggers from the diary. All three are RBAC-gated (appointments:write + renter-own/manager-any)
// and sealed-book scoped exactly like the auto path.

/**
 * Manually OFFER this WAITING entry's slot to that specific entry. Carries no body — the entry already
 * names its service/staff/date; the offer reuses the SAME gap-offer comms path (consent-aware,
 * allowance-enforced, pause-never-auto-spend) and idempotency key (`waitlist-offer:<id>`) as the auto
 * offer-on-cancel. Flips the entry waiting→offered.
 */
export const OfferWaitlistEntry = z.object({}).meta({ id: 'OfferWaitlistEntry' });
export type OfferWaitlistEntry = z.infer<typeof OfferWaitlistEntry>;

/**
 * CONVERT a waitlist entry into a REAL appointment. The entry only stores a wantedDate (a full DATE),
 * so the operator supplies the concrete startAt of the chosen slot; the appointment's service + staff
 * come from the entry. The create runs the SAME availability/clash guards as POST /v1/appointments
 * (an unavailable time is rejected with the same 409), and flips the entry status→'booked' in the SAME
 * transaction as the appointment insert. Idempotent on a double-click: a converted entry never creates
 * a second appointment. Optional idempotencyKey threads into the appointment-create idempotency.
 */
export const ConvertWaitlistEntry = z
  .object({
    startAt: IsoDateTime.describe('the concrete slot start to book (the entry only stores a date)'),
    endAt: IsoDateTime.nullish().describe('optional override; otherwise derived from the service'),
    idempotencyKey: z.string().min(8).max(200).optional(),
  })
  .meta({ id: 'ConvertWaitlistEntry' });
export type ConvertWaitlistEntry = z.infer<typeof ConvertWaitlistEntry>;

/**
 * EXPIRE (release) an OFFERED entry → status 'expired'. This is ALSO the "decline" action — there is no
 * 'declined' enum value, a declined offer simply expires. Optionally re-runs offerBestMatch for the next
 * best match on the freed day, reusing the same gap-offer send path.
 */
export const ExpireWaitlistEntry = z.object({}).meta({ id: 'ExpireWaitlistEntry' });
export type ExpireWaitlistEntry = z.infer<typeof ExpireWaitlistEntry>;
